import { DownloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Descriptions, Row, Skeleton, Space, Statistic, Table, Tag, message } from 'antd';
import { downloadFile, errorMessage } from '../../api/client';
import { useDealerLedger } from '../../api/hooks';
import type { DealerLedgerEntry } from '../../types';
import { date, money } from '../../utils/format';
import { useTranslation } from 'react-i18next';

const TYPE_COLOR: Record<string, string> = {
  Placement: 'blue',
  PrincipalReturn: 'orange',
  ProfitReceipt: 'green',
};

const TYPE_LABEL: Record<string, string> = {
  Placement: 'Investment',
  PrincipalReturn: 'Withdrawal',
  ProfitReceipt: 'ProfitPayment',
};

/** Mirrors the investor Ledger tab exactly — same cards, same descriptions block, same table shape. */
export default function DealerLedgerTab({ dealerId }: { dealerId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useDealerLedger(dealerId);

  if (isLoading || !data) return <Skeleton active />;

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(`/reports/dealer-ledger/${dealerId}`, `ledger_statement_${dealerId}.pdf`).catch((err) =>
              message.error(errorMessage(err)),
            )
          }
        >
          Download ledger statement (PDF)
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={t('finance.totalInvestment')} value={money(data.currentPrincipal)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={t('finance.totalProfit')} value={money(data.outstandingProfit)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Total due to me"
              value={money(data.totalDue)}
              prefix="PKR"
              valueStyle={{ color: '#FE9F43' }}
            />
          </Card>
        </Col>
      </Row>

      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} style={{ marginTop: 16 }}>
        <Descriptions.Item label={t('dashboard.firstInvestment')}>
          {data.firstPlacementDate ? date(data.firstPlacementDate) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('table.asOf')}>{date(data.asOf)}</Descriptions.Item>
        <Descriptions.Item label={t('finance.totalInvestment')}>{money(data.totalGiven)}</Descriptions.Item>
        <Descriptions.Item label={t('finance.totalWithdrawn')}>{money(data.totalReturned)}</Descriptions.Item>
        <Descriptions.Item label={t('finance.totalProfit')}>{money(data.totalAccruedProfit)}</Descriptions.Item>
        <Descriptions.Item label="Total paid profit">{money(data.totalReceivedProfit)}</Descriptions.Item>
      </Descriptions>

      <Card title={t('table.transactions')} size="small" style={{ marginTop: 16 }}>
        <Table<DealerLedgerEntry>
          rowKey={(row, index) => `${row.date}-${row.type}-${index}`}
          size="small"
          dataSource={data.entries}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          columns={[
            { title: t('common.date'), dataIndex: 'date', render: date },
            {
              title: t('common.type'),
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{TYPE_LABEL[value] ?? value}</Tag>,
            },
            { title: t('party.description'), dataIndex: 'description' },
            { title: t('table.debit'), dataIndex: 'given', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: t('table.credit'), dataIndex: 'received', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Investment', dataIndex: 'runningPrincipal', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
